import { Action } from "redux";
import { Task } from "redux-saga";
import { ActionPattern, call, cancel, fork, join, takeEvery } from "redux-saga/effects";
import { RootState } from "src/store";
import { select, take } from "typed-redux-saga";

export function* getCurrentCustomer() {
  const currentCustomer = yield* select((state: RootState) => state.generalReducer.applicationReducer.currentCustomer);
  if (currentCustomer.loading !== "has-loaded") throw new Error("Customer has not loaded yet");
  return currentCustomer.data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* takeLatestPerKey<A extends Action, K extends string | number>(pattern: ActionPattern<A>, worker: (action: A) => any, keySelector: (action: A) => K) {
  return fork(function* () {
    const tasks: Record<string | number, Task> = {};

    yield takeEvery<A>(pattern, function* (action) {
      const key: K = yield call<typeof keySelector>(keySelector, action);

      if (tasks[key]) {
        yield cancel(tasks[key]);
      }

      tasks[key] = yield fork<typeof worker>(worker, action);

      yield join(tasks[key]);

      if (tasks[key] && !tasks[key].isRunning()) {
        delete tasks[key];
      }
    });
  });
}

// One write at a time per key, callers queued in arrival order.
//
// A debounce that cancels the previous task only protects the waiting phase. redux-saga's cancel() drops
// the response of a request already on the wire; it does not un-send it, and the generated client's promise
// carries no [CANCEL] handle, so the server applies it regardless. A write that outlives its own debounce
// window therefore ends up running alongside the next one — which is how two saves of the same attribute
// both read "no row for this part" before either inserted, and the second insert hit the unique index on
// (AttributeId, EntityId, DimensionPart). It also let an older response land last and re-seed the form with
// values the user had already replaced.
//
// The queue is a promise chain rather than saga tasks on purpose: a caller claims its slot SYNCHRONOUSLY,
// before its first yield, so two debounce timers firing in the same tick cannot both find the key idle.
// Run this detached (spawn, not fork) — once a write is queued it must not be cancellable.
const writeChainByKey = new Map<string, Promise<void>>();

const sequenceByKey = new Map<string, number>();

// Serializing the requests is only half of it. Every response re-seeds the form, so an EARLIER write
// answering while a later one is still queued would reset the field the user has since typed into, and the
// later response would fill it back in — a visible flash of the old value. Cancelling used to hide this by
// discarding the superseded response; the queue cannot cancel, so the response is dropped explicitly
// instead. Claim at the moment the action ARRIVES, not when its write starts: the newer edit is what makes
// the older response stale, and it has to count from the instant it happens.
export const claimLatestWrite = (key: string) => {
  const mine = (sequenceByKey.get(key) ?? 0) + 1;
  sequenceByKey.set(key, mine);
  return () => sequenceByKey.get(key) !== mine;
};

export function* runInTurn<A>(key: string, worker: (action: A, isSuperseded: () => boolean) => unknown, action: A, isSuperseded: () => boolean) {
  const previous = writeChainByKey.get(key);

  let release: () => void = () => undefined;
  const mine = new Promise<void>(resolve => {
    release = resolve;
  });
  writeChainByKey.set(key, mine);

  try {
    if (previous) yield call(() => previous);
    yield call(worker, action, isSuperseded);
  } finally {
    release();
    // Only if nobody has queued behind this one — deleting unconditionally would drop a later caller's slot
    // and let the one after it start early.
    if (writeChainByKey.get(key) === mine) writeChainByKey.delete(key);
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* takeLeadingPerKey<A extends Action>(pattern: ActionPattern<A>, worker: (action: A) => any, keySelector: (action: A) => string | number) {
  const tasks: Record<string | number, Task> = {};

  while (true) {
    const action: A = yield take(pattern);
    const key = keySelector(action);

    if (!tasks[key] || !tasks[key].isRunning()) {
      tasks[key] = yield fork(function* () {
        try {
          yield* worker(action);
        } finally {
          delete tasks[key]; // Clean up finished task
        }
      });
    }
  }
}
